import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import { Flex, Link, SliderField, Typography } from '@iheartjane/reefer';
import { useCallback, useMemo, useState } from 'react';

import { selectedFilterRange } from '@jane/kiosk/util';
import type {
  FilterGroup,
  FilterRangeItem,
  JaneSearchState,
  ProductFilterKeys,
  RangeFilterValue,
} from '@jane/search/data-access';

import { Grid } from '../Grid/Grid';

interface FilterSliderProps {
  item: FilterRangeItem;
  onChange: (key: ProductFilterKeys, value: RangeFilterValue) => void;
  onDeselect: (key: ProductFilterKeys) => void;
  searchState: JaneSearchState<MaterializedMenuProduct>;
}

const FilterSlider = ({
  item,
  onChange,
  onDeselect,
  searchState,
}: FilterSliderProps) => {
  const minMax = useMemo(() => {
    const max = Math.ceil(Number(item.max));
    const min = Math.floor(Number(item.min));
    return {
      max,
      min,
    };
  }, [item]);

  const isFieldDisabled = useMemo(() => {
    const { max, min } = minMax;

    const isSameValue = max === min;
    const isLowRange = minMax.min === 0 && minMax.max === 1;

    return isSameValue || isLowRange;
  }, [minMax]);

  const [displayValue, setDisplayValue] = useState({
    ...minMax,
    ...selectedFilterRange(searchState, item.key),
  });

  const handleDeselect = useCallback(() => {
    onDeselect(item.key);
    setDisplayValue(minMax);
  }, [item.key, minMax, onDeselect]);

  const handleChange = useCallback(() => {
    onChange(item.key, displayValue);
  }, [displayValue, item, onChange]);

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-between">
        <Typography mb={16} variant="h3-semibold">
          {item.label}
        </Typography>
        {!isFieldDisabled && (
          <Link
            onClick={handleDeselect}
            ariaLabel={`${item.label} - deselect all`}
          >
            <Typography>Deselect all</Typography>
          </Link>
        )}
      </Flex>
      <SliderField
        defaultValue={displayValue}
        disabled={isFieldDisabled}
        endUnit="%"
        isDouble
        label={item.label}
        labelHidden
        max={minMax.max}
        min={minMax.min}
        name={item.label}
        onChange={setDisplayValue}
        onAfterChange={handleChange}
        step={1}
        value={displayValue}
        width="100%"
      />
    </Flex>
  );
};

interface FilterRangesProps {
  filterGroup: FilterGroup;
  onChange: (key: ProductFilterKeys, value: RangeFilterValue) => void;
  onDeselect: (key: ProductFilterKeys) => void;
  searchState: JaneSearchState<MaterializedMenuProduct>;
}

export const FilterRanges = ({
  filterGroup,
  onChange,
  onDeselect,
  searchState,
}: FilterRangesProps) => {
  if (
    filterGroup.type !== 'range' ||
    !filterGroup.nested /** the only existing set of range filters are currently nested */
  ) {
    return null;
  }

  const { items } = filterGroup;

  return (
    <Grid gap={24} gridTemplateColumns={`repeat(${items.length}, 1fr)`}>
      {items.map((item) => (
        <FilterSlider
          item={item}
          key={`${item.key}-${item.min}-${item.max}`}
          onChange={onChange}
          onDeselect={onDeselect}
          searchState={searchState}
        />
      ))}
    </Grid>
  );
};
